import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Loader2, RefreshCw, LogOut } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { listLeads } from "@/lib/leads.functions";
import { Section } from "@/components/Section";

export const Route = createFileRoute("/admin/leads")({
  head: () => ({
    meta: [
      { title: "Leads — Freelancer Tamal" },
      { name: "description", content: "Private inbox of website enquiries." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Leads — Freelancer Tamal" },
      { property: "og:description", content: "Private inbox of website enquiries." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: LeadsPage,
});

function LeadsPage() {
  const navigate = useNavigate();
  const fetchLeads = useServerFn(listLeads);
  const [signedIn, setSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => setSignedIn(!!session));
    void supabase.auth.getSession().then(({ data }) => setSignedIn(!!data.session));
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (signedIn === false) void navigate({ to: "/auth" });
  }, [signedIn, navigate]);

  const query = useQuery({
    queryKey: ["admin-leads"],
    queryFn: () => fetchLeads(),
    enabled: signedIn === true,
  });

  const leads = query.data?.leads ?? [];

  return (
    <>
      <h1 className="sr-only">Captured leads</h1>
      <Section eyebrow="Private" title={<>Lead <span className="text-gradient">inbox.</span></>}>
        <div className="-mt-8 flex flex-wrap items-center gap-4">
          <button
            onClick={() => void query.refetch()}
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm hover:border-primary/60"
          >
            <RefreshCw size={14} className={query.isFetching ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={async () => {
              await supabase.auth.signOut();
              void navigate({ to: "/auth" });
            }}
            className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm hover:border-primary/60"
          >
            <LogOut size={14} /> Sign out
          </button>
          <span className="text-sm text-muted-foreground">
            {query.isSuccess ? `${leads.length} submission${leads.length === 1 ? "" : "s"}` : ""}
          </span>
        </div>

        {signedIn !== true || query.isPending ? (
          <p className="mt-10 inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : query.isError ? (
          <div className="mt-10 rounded-2xl border border-destructive/40 bg-destructive/5 p-6 text-sm">
            <p className="font-medium">Couldn't load leads.</p>
            <p className="mt-1 text-muted-foreground">
              {String(query.error).includes("Forbidden")
                ? "This account isn't an admin. Ask for the admin role to be added to it."
                : String(query.error)}
            </p>
            <Link to="/auth" className="mt-3 inline-block underline">Sign in with a different account</Link>
          </div>
        ) : leads.length === 0 ? (
          <p className="mt-10 text-muted-foreground">No submissions yet.</p>
        ) : (
          <ul className="mt-10 space-y-4">
            {leads.map((l) => (
              <li key={l.id} className="rounded-2xl border border-border bg-card p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div className="font-display text-lg">
                    {l.name ?? "—"}{" "}
                    <a href={`mailto:${l.email}`} className="text-sm text-primary underline underline-offset-4">
                      {l.email}
                    </a>
                  </div>
                  <time dateTime={l.created_at} className="text-xs text-muted-foreground">
                    {new Date(l.created_at).toLocaleString()}
                  </time>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                  <span>Source: {l.source}</span>
                  {l.company && <span>Company: {l.company}</span>}
                  {l.budget && <span>Budget: {l.budget}</span>}
                  {l.page_path && <span>Page: {l.page_path}</span>}
                </div>
                {l.message && <p className="mt-3 whitespace-pre-wrap text-sm">{l.message}</p>}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}
